// @flow
import React from 'react'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import OlMap from 'ol/Map'
import type { MapfileType } from 'catena/er2_map/src/map'
import type { MapsourcesType } from 'catena/er2_map_userlayers/js/reducers/mapsources'
import ArcgisrestLayer from './arcgisrest_layer'
import TimeseriesLayer from './timeseries_layer'
import Layer from './layer'
import Table from './table'
import type { MapType } from '../reducers/map'
import { getMapserver } from '../utils'

// A group represents a mixed collection of userlayers
type GroupType = {
    items: [UserMapsourceType],
}

type PropsType = {
    baseImageURL: string,
    er2Map: any,
    getUserLayerClassifications: Function,
    group: GroupType,
    loadingTileCounts: {},
    map: MapType,
    mapserverHost: string,
    mapsources: MapsourcesType,
    olMap: OlMap,
    onChangeSortOrderForGroup: Function,
    onDeletePublicService: Function,
    onServiceDownload: Function,
    onServicePropsChange: Function,
    onUserLayerDelete: Function,
    onUserLayerDownload: Function,
    onUserLayerGetAttributes: Function,
    onUserLayersPropsChange: Function,
    onUserLayersZoom: Function,
    theme: {},
}

class GroupPanel extends React.PureComponent<PropsType> {
    /**
     * Make sure that service layers are not sandwiched between mapserver layers because mapserver layers
     * are displayed as a block.
     * @param ordering
     * @returns {*[]}
     */
    organizeOrdering(ordering) {
        let iFirst
        let iLast
        ordering.forEach((o, index) => {
            if (o.type !== 'arcgisrest') {
                // Assumes mapserver. Check if the first mapserver layer has been found
                if (iFirst === undefined) iFirst = index
                else iLast = index
            }
        })
        const orderingStart = []
        const orderingMapserver = []
        const orderingEnd = []
        ordering.forEach((o, index) => {
            if (o.type === 'arcgisrest') {
                if (index < iFirst) orderingStart.push(o)
                else orderingEnd.push(o)
            } else {
                orderingMapserver.push(o)
            }
        })
        return [...orderingStart, ...orderingMapserver, ...orderingEnd]
    }

    render() {
        // Build layer controls
        // [layer1, [folder1, [layer2, layer2] ], layer3]

        const defaultBackgroundColor = '#eeeeee'
        const getItemStyle = (draggableStyle, isDragging) => ({
            // some basic styles to make the items look a bit nicer
            userSelect: 'none',
            border: '1px solid gray',
            padding: '2px',
            margin: '2px',
            // change background colour if dragging
            background: isDragging ? 'lightgreen' : defaultBackgroundColor,
            // styles we need to apply on draggables
            ...draggableStyle,
        })
        const getListStyle = isDraggingOver => ({
            background: isDraggingOver ? 'lightblue' : defaultBackgroundColor,
        })

        const onDragEnd = (result) => {
            if (result.destination) {
                const group = this.props.group
                const reordered = group.layers.slice()
                const moved = reordered.splice(result.source.index, 1)[0]
                reordered.splice(result.destination.index, 0, moved)
                // Reverse layer order to match original input
                const organizedOrdering = this.organizeOrdering(reordered)
                this.props.onChangeSortOrderForGroup(group.name, organizedOrdering)
            }
        }

        const onChangeProps = (layer, layerprops) => {
            this.props.onUserLayersPropsChange(layer.mapsource, layer.name, layerprops)
        }

        const onChangeServiceProps = (site, service, serviceprops) => {
            this.props.onServicePropsChange(site, service, serviceprops)
        }

        const getItems = () => {
            const mapserver = getMapserver(this.props.mapsources)
            const group = this.props.group
            return group.layers.map((layer, index) => {
                // Currently we can display two sources, mapserver and arcgisrest
                const layerType = layer.type === undefined ? 'mapserver' : layer.type
                // Groups can be ordered
                return (
                    <Draggable
                        key={`${layer.name}-${index}`}
                        draggableId={layer.name}
                        index={index}
                    >
                        {(provided, snapshot) => (
                            <div
                                ref={provided.innerRef}
                                style={getItemStyle(
                                    provided.draggableProps.style,
                                    snapshot.isDragging,
                                )}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                            >
                                {layerType === 'mapserver' && layer.layer_type === 'table' && (
                                    <Table
                                        {...layer}
                                        key={layer.name}
                                        onChangeProps={layerprops => onChangeProps(layer, layerprops)}
                                        onDelete={() => this.props.onUserLayerDelete(layer.mapsource, layer.name)}
                                        onDownload={() => this.props.onUserLayerDownload(layer.mapsource, layer.name)}
                                        onGetAttributes={() => this.props.onUserLayerGetAttributes(layer.mapsource, layer.name)}
                                        theme={this.props.theme}
                                    />
                                )}
                                {layerType === 'mapserver' && layer.layer_type !== 'table' && (
                                    <Layer
                                        {...layer}
                                        er2Map={this.props.er2Map}
                                        key={layer.name}
                                        map={this.props.map}
                                        onChangeProps={layerprops => onChangeProps(layer, layerprops)}
                                        onGetAttributes={() => this.props.onUserLayerGetAttributes(layer.mapsource, layer.name, false)}
                                        onQuery={() => this.props.onUserLayerGetAttributes(layer.mapsource, layer.name, true)}
                                        onZoom={() => this.props.onUserLayersZoom(layer.mapsource, [layer.name])}
                                        onDelete={() => this.props.onUserLayerDelete(layer.mapsource, layer.name)}
                                        onDownload={() => this.props.onUserLayerDownload(layer.mapsource, layer.name)}
                                        getLayerClassifications={(...args) => this.props.getUserLayerClassifications(layer.mapsource, layer.name, ...args)}
                                        legendURL={`${this.props.mapserverHost}/cgi-bin/mapserv?SERVICE=WMS&VERSION=1.1.1&REQUEST=GetLegendGraphic&LAYER=${encodeURIComponent(layer.name)}&Format=image/png&map=${mapserver.mapfile}&foo=${Math.random()}`}
                                        theme={this.props.theme}
                                    />
                                )}
                                {layerType === 'arcgisrest' && (
                                    <ArcgisrestLayer
                                        {...layer}
                                        baseImageURL={this.props.baseImageURL}
                                        er2Map={this.props.er2Map}
                                        key={layer.name}
                                        loadingTileCounts={this.props.loadingTileCounts[layer.mapsource]}
                                        onChangeProps={serviceprops => onChangeServiceProps(layer.site_name, layer.service_name, serviceprops)}
                                        onDelete={() => this.props.onDeletePublicService(layer)}
                                        onDownload={boundary => this.props.onServiceDownload(layer, boundary)}
                                        theme={this.props.theme}
                                    />
                                )}
                                {layerType === 'timeseries' && (
                                    <TimeseriesLayer
                                        {...layer}
                                        key={layer.name}
                                        onChangeProps={serviceprops => onChangeServiceProps(layer.site_name, layer.service_name, serviceprops)}
                                        onDelete={() => this.props.onDeleteTimeseries(layer)}
                                        onDownload={() => this.props.onServiceDownload(layer)}
                                        theme={this.props.theme}
                                    />
                                )}
                            </div>
                        )}
                    </Draggable>
                )
            })
        }

        const panelStyle = {
            fontSize: 'smaller',
            padding: '2px 2px',
            width: '100%',
        }
        return (
            <div style={panelStyle}>
                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="group_layer">
                        {(provided, snapshot) => (
                            <div
                                ref={provided.innerRef}
                                style={getListStyle(snapshot.isDraggingOver)}
                            >
                                {getItems()}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            </div>
        )
    }
}

export default GroupPanel
